import _ from "underscore";

import { isImplicitDeleteAction } from "metabase/actions/utils";
import { isNotNull } from "metabase/lib/types";
import { isEmpty } from "metabase/lib/validate";
import type {
  ActionDashboardCard,
  ActionParameterValue,
  ActionParametersMapping,
  Parameter,
  ParameterId,
  ParameterValueOrArray,
  ParameterValuesMap,
  ParametersForActionExecution,
  WritebackAction,
  WritebackParameter,
} from "metabase-types/api";

type ActionParameterTuple = [ParameterId, ActionParameterValue];

function formatParameterValue(value: ParameterValueOrArray) {
  return Array.isArray(value) ? value.filter(isNotNull)[0] : value;
}

function prepareParameter(
  mapping: ActionParametersMapping,
  action: WritebackAction,
  parameterValues: ParameterValuesMap,
): ActionParameterTuple | undefined {
  if (!action?.parameters?.length) {
    return;
  }
  const { parameter_id: sourceParameterId, target: actionParameterTarget } =
    mapping;

  const parameterValue = parameterValues[sourceParameterId];
  const actionParameter = action.parameters.find((parameter) =>
    _.isEqual(parameter.target, actionParameterTarget),
  );

  // don't return unmapped or empty values
  if (!actionParameter || isEmpty(parameterValue)) {
    return;
  }

  return [actionParameter.id, formatParameterValue(parameterValue)];
}

export function getDashcardParamValues(
  dashcard: ActionDashboardCard,
  parameterValues: ParameterValuesMap,
): ParametersForActionExecution {
  if (
    !dashcard.action ||
    !dashcard?.parameter_mappings?.length ||
    !parameterValues
  ) {
    return {};
  }
  const { action, parameter_mappings } = dashcard;

  return Object.fromEntries(
    parameter_mappings
      ?.map((mapping) => prepareParameter(mapping, action, parameterValues))
      ?.filter(isNotNull),
  );
}

function isMappedParameter(
  parameter: WritebackParameter,
  dashboardParamValues: ParametersForActionExecution,
) {
  return parameter.id in dashboardParamValues;
}

export function getNotProvidedActionParameters(
  action: WritebackAction,
  dashboardParamValues: ParametersForActionExecution,
) {
  // return any action parameters that don't have mapped values
  return (action.parameters ?? []).filter((parameter) => {
    if ("default" in parameter) {
      return false;
    }
    return !isMappedParameter(parameter, dashboardParamValues);
  });
}

export function getMappedActionParameters(
  action: WritebackAction,
  dashboardParamValues: ParametersForActionExecution,
) {
  const parameters = action.parameters ?? [];
  return parameters.filter((parameter) => {
    return isMappedParameter(parameter, dashboardParamValues);
  });
}

/**
 * Maps action parameter id -> the dashboard filter wired up to it.
 *
 * The dashcard stores the reverse: each mapping holds the dashboard filter's
 * `parameter_id` and a `target` matching one of the action's parameters.
 */
export function getDashboardParametersByActionParameter(
  dashcard: ActionDashboardCard,
  dashboardParameters: Parameter[],
): Record<ParameterId, Parameter> {
  const actionParameters = dashcard.action?.parameters ?? [];
  const mappings = dashcard.parameter_mappings ?? [];

  const entries = mappings
    .map((mapping) => {
      const actionParameter = actionParameters.find((parameter) =>
        _.isEqual(parameter.target, mapping.target),
      );
      const dashboardParameter = dashboardParameters.find(
        (parameter) => parameter.id === mapping.parameter_id,
      );

      if (!actionParameter || !dashboardParameter) {
        return null;
      }

      return [actionParameter.id, dashboardParameter] as const;
    })
    .filter(isNotNull);

  return Object.fromEntries(entries);
}

export const shouldShowConfirmation = (action?: WritebackAction) => {
  if (!action) {
    return false;
  }
  const hasConfirmationMessage =
    !!action.visualization_settings?.confirmMessage;
  return hasConfirmationMessage || isImplicitDeleteAction(action);
};

export const isParameterHidden = (
  action: WritebackAction,
  parameter: WritebackParameter,
) => {
  return !!action.visualization_settings?.fields?.[parameter.id]?.hidden;
};

export const isParameterRequired = (
  action: WritebackAction,
  parameter: WritebackParameter,
) => {
  return !!(
    parameter.required ||
    action.visualization_settings?.fields?.[parameter.id]?.required
  );
};

export const getParameterDefaultValue = (
  action: WritebackAction,
  parameter: WritebackParameter,
) => {
  return action.visualization_settings?.fields?.[parameter.id]?.defaultValue;
};

function getHashParams(): URLSearchParams {
  return new URLSearchParams(window.location.hash.replace(/^#/, ""));
}

export function shouldAutoOpenAction(action: WritebackAction): boolean {
  const openActionId = getHashParams().get("openAction");
  return openActionId != null && Number(openActionId) === action.id;
}

export function getParamValuesFromUrl(
  action: WritebackAction,
): ParametersForActionExecution {
  const hashParams = getHashParams();
  const values: ParametersForActionExecution = {};

  action.parameters?.forEach((parameter) => {
    const slug = (parameter as { slug?: string }).slug;
    const raw =
      (slug != null ? hashParams.get(`actionParam_${slug}`) : null) ??
      hashParams.get(`actionParam_${parameter.id}`);

    if (raw === null) {
      return;
    }
    values[parameter.id] = parameter.type?.startsWith("number")
      ? Number(raw)
      : raw;
  });

  return values;
}

export function shouldAutoSubmitAction(action: WritebackAction): boolean {
  const autoSubmit = getHashParams().get("autoSubmit");
  return autoSubmit === "1" && shouldAutoOpenAction(action);
}
